import { useEffect, useState } from 'react';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';
import { useParams, useSearchParams } from 'react-router-dom';
import { dashboardServiceConfig } from '../../utils/supportServiceConfig/dashboardServiceConfig';
import { getModeOrg, getTypeOrg } from '../../utils/getType';
import { supBookingsServiceConfig } from '../../utils/supportServiceConfig/supBookingsServiceConfig';
import { getTaxAmount } from '../../utils/getAmountWithTransaction';
import handleNestedChange from '../../utils/handleNestedChange';
import BookingReadOnly from './BookingReadOnly';
import { toast } from 'react-toastify';
import { supBookingUpdate } from '../../api/supportAdmin';
import { FetchBookingData, FetchPackList, fetchTripData, fetchTripDate } from './fetchData';
import { getOrganizers } from '../../utils/getOrganizers';
import { getTotalSeatCount } from '../../utils/getTotalSeatCount';
import PriceModel from './PriceModel';

const SupBookingEditAct = () => {
    const isMobile = window.innerWidth < 768;
    const [focusedInput, setFocusedInput] = useState(null);
    const [searchParams] = useSearchParams();
    const { mode } = useParams();
    const bookingId = searchParams.get('bookingId');
    const orgId = searchParams.get('orgId');
    const actId = searchParams.get('packId');
    const [booking, setBooking] = useState({});
    const [showPrice, setShowPrice] = useState(false);
    const [packData, setPackData] = useState(null);
    const [packDates, setPackDates] = useState([]);
    const [orgData, setOrgData] = useState([]);
    const [bookingType, setBookingType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [trips, setTrips] = useState([]);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [actDatesId, setActDatesId] = useState('');
    const [slotId, setSlotId] = useState('');
    const [meetUpPoints, setMeetUpPoints] = useState([]);
    const [timeSlots, setTimeSlots] = useState([]);
    const formatDate = (date) => {
        return new Date(date).toISOString().split('T')[0];
    };
    const typeBooking = [
        'Same org - Same Activity - Different date',
        'Same org - Different Activity',
        'Different org - Different Activity'
    ];

    const serviceConfig = dashboardServiceConfig[getTypeOrg(mode)] || {};
    const serviceBookingConfig = supBookingsServiceConfig[getTypeOrg(mode)];
    const [newAmountPaid, setNewAmountPaid] = useState({});

    useEffect(() => {
        FetchBookingData(setBooking, 'act', bookingId, setLoading);
        getOrganizers('act', setOrgData, setLoading);
    }, []);

    useEffect(() => {
        const id = selectedOrg?._id || orgId;
        if (!id) return;
        setSelectedDate('');
        FetchPackList('act', setTrips, id, 'active', 10, 0, setLoading);
    }, [selectedOrg, orgId]);

    useEffect(() => {
        if (booking?.actId && booking?.actId !== '-1') {
            fetchTripData('act', selectedTrip || actId, setLoading, setPackData, setPackDates);
        }
    }, [actId, selectedTrip]);

    useEffect(() => {
        if (!selectedDate || !packDates?.length) return;
        const selectedDateObj = packDates.find((item) => formatDate(item.date) === formatDate(selectedDate));
        setActDatesId(selectedDateObj?._id || '');
        setTimeSlots(selectedDateObj?.slots || []);
    }, [selectedDate, packDates]);

    useEffect(() => {
        if (!selectedDate) return;

        const getTripDate = async () => {
            const date = new Date(selectedDate);

            // set same date, only time
            date.setUTCHours(18, 30, 0, 0);

            const finalDate = date.toISOString();

            const tripDate = await fetchTripDate({ date: finalDate }, 'act');
            const availableSlots = tripDate?.slots.filter(
                (slot) => slot.availableSlots > 0 && slot.totalSlots - slot.bookedSlots > 0
            );
            setActDatesId(tripDate?._id || '');

            setTimeSlots(availableSlots || []);
        };
        setMeetUpPoints(packData?.meetupLocation);
        getTripDate();
    }, [selectedDate]);

    useEffect(() => {
        if (!packData) return;
        const calculatedPrices = booking.totalSeats.map((seat) => {
            const matchedPrice = packData?.price?.find((priceItem) => priceItem.name === seat.name);

            if (!matchedPrice) return null;

            return {
                name: seat.name,
                qty: seat.qty,
                count: matchedPrice.count,
                actual: matchedPrice.actual * seat.qty,
                tax: matchedPrice.tax * seat.qty,
                total: matchedPrice.total * seat.qty,
                reserve: matchedPrice.reserve
            };
        });
        console.log(calculatedPrices);
        const actual = packData?.price[0].actual * getTotalSeatCount(booking.totalSeats);
        const tax = getTaxAmount(actual);
        const total = actual + tax;
        const paid =
            booking?.amountPaid?.paid > newAmountPaid?.total ? newAmountPaid?.total : booking?.amountPaid?.paid;
        setNewAmountPaid({ actual, tax, total, paid });
    }, [selectedDate, packData]);

    const filteredOrg = orgData.filter((org) => org._id !== orgId);
    const filteredTrip = trips.filter((trip) => trip._id !== actId);

    const handleSubmit = async () => {
        let finalData;
        let org_Id;
        if (bookingType === 'Same org - Same Activity - Different date') {
            org_Id = orgId;
            finalData = {
                ...finalData,
                newActDatesId: actDatesId,
                startDate: selectedDate,
                endDate: selectedDate,
                newSlotId: slotId,
                org: 'same',
                trip: 'same'
            };
        }

        if (bookingType === 'Same org - Different Activity') {
            org_Id = orgId;
            finalData = {
                ...finalData,
                newSlotId: slotId,
                startDate: selectedDate,
                endDate: selectedDate,
                newActDatesId: actDatesId,
                newActId: selectedTrip,
                org: 'same',
                trip: 'different'
            };
        }

        if (bookingType === 'Different org - Different Activity') {
            org_Id = selectedOrg;
            finalData = {
                ...finalData,
                newSlotId: slotId,
                newActDatesId: actDatesId,
                startDate: selectedDate,
                endDate: selectedDate,
                newActId: selectedTrip,
                newOrganizerId: selectedOrg,
                org: 'different',
                trip: 'different'
            };
        }

        try {
            console.log(finalData, 'Submission');
            const result = await supBookingUpdate('act', bookingId, finalData);
            if (result.status == 200) {
                toast.success('Booking changed successfully');
                //navigate(`/support/bookings/${org_Id}`);
            }
        } catch (error) {
            console.error('Booking update failed:', error.response.data);
            toast.error(error.response.data.message);
        }
    };

    return (
        <div className='bookingEditDiv'>
            <div className='dropRegDiv'>
                <label className='dropRegLabel'>Type to Change</label>
                <div className='dropRegInputDiv'>
                    <select
                        value={bookingType}
                        name='bookingType'
                        onChange={(e) => {
                            setBookingType(e.target.value);
                            setSelectedDate(null);
                            setSelectedOrg(null);
                            setSelectedTrip(null);
                        }}
                        required={true}
                        className='dropRegSelect'
                    >
                        {' '}
                        <option value=''>Select type</option>
                        {typeBooking?.map(function (val, typeIndex) {
                            return (
                                <option key={typeIndex} value={val}>
                                    {val}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>
            {bookingType === 'Different org - Different Activity' && (
                <>
                    <div className='dropRegDiv'>
                        <label className='dropRegLabel'>Select Organizer</label>
                        <div className='dropRegInputDiv'>
                            <select
                                name='org'
                                id='org'
                                className='dropRegSelect'
                                value={selectedOrg}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedOrg(value);
                                }}
                            >
                                <option value=''>Select Organizer</option>
                                {filteredOrg?.map((org) => (
                                    <option value={org._id} key={org._id}>
                                        {org.companyName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className='dropRegDiv'>
                        <label className='dropRegLabel'>Select Activity</label>
                        <div className='dropRegInputDiv'>
                            <select
                                name='trip'
                                id='trip'
                                className='dropRegSelect'
                                value={selectedTrip}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedTrip(value);
                                }}
                            >
                                <option value=''>Select Activity</option>
                                {filteredTrip?.map((trip) => (
                                    <option value={trip._id} key={trip._id}>
                                        {trip.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </>
            )}

            {bookingType === 'Same org - Different Activity' && (
                <div className='dropRegDiv'>
                    <label className='dropRegLabel'>Select Activity</label>
                    <div className='dropRegInputDiv'>
                        <select
                            name='trip'
                            id='trip'
                            className='dropRegSelect'
                            value={selectedTrip}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSelectedTrip(value);
                            }}
                        >
                            <option value=''>Select Activity</option>
                            {filteredTrip?.map((trip) => (
                                <option value={trip._id} key={trip._id}>
                                    {trip.title}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            {bookingType && (
                <>
                    <div className='dropRegDiv'>
                        <label className='dropRegLabel'>Select Date</label>
                        <div className='dropRegInputDiv'>
                            <SingleDatePicker
                                withPortal={isMobile}
                                date={selectedDate ? moment(selectedDate) : null}
                                onDateChange={(date) => {
                                    if (!date) return;
                                    setSelectedDate(date.toISOString());
                                    setFocusedInput(false);
                                }}
                                focused={focusedInput}
                                onFocusChange={({ focused }) => setFocusedInput(focused)}
                                displayFormat='DD-MM-YYYY'
                                numberOfMonths={1}
                                isOutsideRange={(day) => day.isBefore(moment().startOf('day'))}
                                showDefaultInputIcon
                                readOnly
                            />
                        </div>
                    </div>
                    {selectedDate && (
                        <>
                            <div className='dropRegDiv'>
                                <label className='dropRegLabel'>Select Slot</label>
                                <div className='dropRegInputDiv'>
                                    <select
                                        value={booking?.timeIndex ?? ''}
                                        name='timeSlots'
                                        onChange={(e) => {
                                            const index = e.target.value;
                                            const selectedSlot = timeSlots[index];
                                            setBooking((prev) => ({
                                                ...prev,
                                                timeIndex: index,
                                                timeSlots: {
                                                    startTime: selectedSlot.startTime,
                                                    endTime: selectedSlot.endTime
                                                },
                                                slotId: selectedSlot._id
                                            }));
                                            setSlotId(selectedSlot._id);
                                        }}
                                        required={true}
                                        className='dropRegSelect'
                                    >
                                        <option value=''>Select Slot</option>

                                        {timeSlots?.map((val, index) => {
                                            return (
                                                <option key={index} value={index}>
                                                    {`${val.startTime} - ${val.endTime}`}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                            <div className='dropRegDiv'>
                                <label className='dropRegLabel'>Select Meetup Location</label>

                                <div className='dropRegInputDiv'>
                                    <select
                                        value={booking?.meetupLocation?.name || ''}
                                        onChange={(e) => {
                                            const selectedLocation = meetUpPoints.find(
                                                (item) => item.name === e.target.value
                                            );

                                            setBooking((prev) => ({
                                                ...prev,
                                                meetupLocation: {
                                                    name: selectedLocation.name,
                                                    location: selectedLocation.location
                                                }
                                            }));
                                        }}
                                        required
                                        className='dropRegSelect'
                                    >
                                        <option value='' disabled>
                                            Select Meetup Point
                                        </option>

                                        {meetUpPoints?.map((val, index) => (
                                            <option key={index} value={val.name}>
                                                {val.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className='dropRegDiv'>
                                <label className='dropRegLabel'>Actual Price</label>
                                <div className='dropRegInputDiv'>
                                    <input
                                        value={
                                            newAmountPaid?.actual ? newAmountPaid?.actual : booking?.amountPaid?.actual
                                        }
                                        name='actual'
                                        onChange={(e) => handleNestedChange(e, setBooking, 'amountPaid')}
                                        type='Text'
                                        placeholder={`Actual Price`}
                                        className='dropRegSelect'
                                        disabled={true}
                                    ></input>
                                </div>
                            </div>
                            <div className='dropRegDiv'>
                                <label className='dropRegLabel'>Tax</label>
                                <div className='dropRegInputDiv'>
                                    <input
                                        value={newAmountPaid?.tax ? newAmountPaid?.tax : booking?.amountPaid?.tax}
                                        name='tax'
                                        onChange={(e) => handleNestedChange(e, setBooking, 'amountPaid')}
                                        type='Text'
                                        placeholder={`Tax`}
                                        className='dropRegSelect'
                                        disabled={true}
                                    ></input>
                                </div>
                            </div>
                            <div className='dropRegDiv'>
                                <label className='dropRegLabel'>Total</label>
                                <div className='dropRegInputDiv'>
                                    <input
                                        value={newAmountPaid?.total ? newAmountPaid?.total : booking?.amountPaid?.total}
                                        name='total'
                                        onChange={(e) => handleNestedChange(e, setBooking, 'amountPaid')}
                                        type='Text'
                                        placeholder={`Total`}
                                        className='dropRegSelect'
                                        disabled={true}
                                    ></input>
                                </div>
                            </div>
                            <div className='dropRegDiv'>
                                <label className='dropRegLabel'>Paid</label>
                                <div className='dropRegInputDiv'>
                                    <input
                                        value={newAmountPaid?.paid ? newAmountPaid?.paid : booking?.amountPaid?.paid}
                                        name='paid'
                                        onChange={(e) => handleNestedChange(e, setBooking, 'amountPaid')}
                                        type='Text'
                                        placeholder={`Paid`}
                                        className='dropRegSelect'
                                        disabled={true}
                                    ></input>
                                </div>
                            </div>
                            {newAmountPaid?.total &&
                                (booking?.amountPaid?.paid > newAmountPaid?.total ? (
                                    <div className='packEditNote' style={{ marginTop: '30px' }}>
                                        <p>
                                            <span style={{ color: 'red', fontWeight: '600' }}>
                                                ₹{booking?.amountPaid?.paid - newAmountPaid?.total}
                                            </span>{' '}
                                            will be credited to the customer's account, as the new trip price is lower
                                            than the previous booking.
                                            <span
                                                onClick={() => setShowPrice(true)}
                                                style={{ cursor: 'pointer', color: 'blue' }}
                                            >
                                                Click here to view price details
                                            </span>
                                        </p>
                                    </div>
                                ) : newAmountPaid?.total === booking?.amountPaid?.paid ? (
                                    <div className='packEditNote' style={{ marginTop: '30px' }}>
                                        <p>
                                            <span style={{ color: 'green', fontWeight: '600' }}>₹0</span> needs to be
                                            paid, as the new trip price is the same as the previous booking.
                                            <span
                                                onClick={() => setShowPrice(true)}
                                                style={{ cursor: 'pointer', color: 'blue' }}
                                            >
                                                Click here to view price details
                                            </span>
                                        </p>
                                    </div>
                                ) : (
                                    <div className='packEditNote' style={{ marginTop: '30px' }}>
                                        <p>
                                            <span style={{ color: 'red', fontWeight: '600' }}>
                                                ₹{newAmountPaid?.total - booking?.amountPaid?.paid}
                                            </span>{' '}
                                            needs to be paid, as the new trip price is higher than the previous booking.
                                            <span
                                                onClick={() => setShowPrice(true)}
                                                style={{ cursor: 'pointer', color: 'blue' }}
                                            >
                                                Click here to view price details
                                            </span>
                                        </p>
                                    </div>
                                ))}

                            {/* <div>
                        Since the trip details have changed, the price has also been updated.
                        
                    </div> */}
                            <BookingReadOnly bookingData={booking} setBookingData={setBooking} />
                            <button type='button' className='orgPackUpSubmitBtn blueBtn' onClick={handleSubmit}>
                                Submit
                            </button>
                        </>
                    )}
                    {showPrice && (
                        <PriceModel bookingData={booking} newAmountPaid={newAmountPaid} setShowPrice={setShowPrice} />
                    )}
                </>
            )}
        </div>
    );
};
export default SupBookingEditAct;
